import {
  Activity,
  Archive,
  ArrowLeftRight,
  BarChart3,
  BedDouble,
  BookOpen,
  Boxes,
  Building2,
  Banknote,
  Calculator,
  Bot,
  Cable,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  Cog,
  Droplets,
  Contact,
  FileClock,
  FlaskConical,
  Gavel,
  Gauge,
  GitBranch,
  GraduationCap,
  Headset,
  HeartPulse,
  IdCard,
  Inbox,
  KeyRound,
  Landmark,
  Lightbulb,
  ListChecks,
  Medal,
  Megaphone,
  Newspaper,
  PawPrint,
  Phone,
  Receipt,
  RefreshCw,
  Scale,
  ScrollText,
  ServerCog,
  ShieldCheck,
  Smartphone,
  Stethoscope,
  Sun,
  Users,
  Video,
  Wallet,
  Siren,
  DoorOpen,
  FileSignature,
  Combine,
  Rocket,
  type LucideIcon,
} from 'lucide-react'
import type { TKey } from '@/i18n'
import type { Me } from '@/api/auth'
import { ACTION_PERMISSION, type ActionId } from '@/api/generated/types'

export interface NavItem {
  id: string
  to: string
  label: TKey
  icon: LucideIcon
  /**
   * Visible when the user may call ANY of these actions (their permissions come from the
   * manifest snapshot, src/api/generated). This mirrors what the backend will allow.
   */
  actions?: ActionId[]
  /** Additionally require one of these roles (super_admin always passes). */
  roles?: string[]
  /** Keyboard shortcut after "g" (g t → Tasks). */
  key?: string
}

export interface NavSection {
  id: string
  label?: TKey
  items: NavItem[]
}

export const NAV: NavSection[] = [
  {
    id: 'main',
    items: [
      { id: 'today', to: '/', label: 'nav.today', icon: Sun, roles: ['admin', 'doctor', 'founder'], key: 'h' },
      { id: 'inquiries', to: '/inquiries', label: 'nav.inquiries', icon: Headset, actions: ['callcenter.inquiries'], key: 'z' },
      { id: 'schedule', to: '/schedule', label: 'nav.schedule', icon: CalendarDays, actions: ['schedule.list'], key: 's' },
      { id: 'journeys', to: '/journeys', label: 'nav.journeys', icon: GitBranch, actions: ['journeys.list'], key: 'j' },
      { id: 'clients', to: '/clients', label: 'nav.clients', icon: PawPrint, actions: ['clients.search'], key: 'c' },
      { id: 'doctor', to: '/doctor', label: 'nav.doctor', icon: Stethoscope, actions: ['assistant.drafts', 'recommendations.approve'], key: 'd' },
      { id: 'calls', to: '/calls', label: 'nav.calls', icon: Phone, actions: ['calls.get'], key: 'l' },
      { id: 'inbox', to: '/inbox', label: 'nav.inbox', icon: Inbox, actions: ['inbox.conversations'], key: 'i' },
      { id: 'finance', to: '/finance', label: 'nav.finance', icon: Wallet, actions: ['finance.reconciliation'], key: 'f' },
      { id: 'checkbox', to: '/finance/checkbox', label: 'nav.checkbox', icon: Receipt, actions: ['checkbox.receipts'] },
      { id: 'money', to: '/finance/money', label: 'nav.money', icon: ArrowLeftRight, actions: ['money.events'] },
      { id: 'bas', to: '/finance/bas', label: 'nav.bas', icon: Calculator, actions: ['bas.status_matrix', 'bas.money_docs', 'bas.employees', 'bas.counterparties', 'bas.sources_list'] },
      { id: 'fincontrol', to: '/fincontrol', label: 'nav.fincontrol', icon: ShieldCheck, actions: ['fincontrol.overview'] },
      { id: 'inventory', to: '/inventory', label: 'nav.inventory', icon: Boxes, actions: ['inventory.stock'], key: 'w' },
      { id: 'callQuality', to: '/call-quality', label: 'nav.callQuality', icon: Medal, actions: ['callquality.leaderboard'] },
      { id: 'callKpi', to: '/call-kpi', label: 'nav.callKpi', icon: Gauge, actions: ['callquality.kpi'] },
      { id: 'quality', to: '/quality', label: 'nav.quality', icon: HeartPulse, actions: ['ratings.followup_resolve', 'ratings.summary'], roles: ['quality', 'founder', 'admin'], key: 'q' },
      { id: 'marketing', to: '/marketing', label: 'nav.marketing', icon: Megaphone, actions: ['segments.list'], key: 'm' },
      { id: 'tasks', to: '/tasks', label: 'nav.tasks', icon: ListChecks, actions: ['tasks.list'], key: 't' },
      { id: 'insights', to: '/insights', label: 'nav.insights', icon: Lightbulb, actions: ['insights.list'], key: 'n' },
    ],
  },
  {
    id: 'processes',
    label: 'nav.processes',
    items: [
      { id: 'process', to: '/process', label: 'nav.processMine', icon: ClipboardCheck, actions: ['process.runs_list'], key: 'k' },
      { id: 'processCalendar', to: '/process/calendar', label: 'nav.processCalendar', icon: CalendarRange, actions: ['process.calendar'] },
      { id: 'processTemplates', to: '/process/templates', label: 'nav.processTemplates', icon: ClipboardList, actions: ['process.template_save'] },
      { id: 'processNorms', to: '/process/norms', label: 'nav.processNorms', icon: Scale, actions: ['process.norm_set'] },
      { id: 'labShipments', to: '/lab/shipments', label: 'nav.labShipments', icon: FlaskConical, actions: ['lab_shipments.list'] },
      { id: 'labSettings', to: '/lab/labs', label: 'nav.labSettings', icon: Building2, actions: ['lab_shipments.lab_save'] },
      { id: 'vrit', to: '/vrit', label: 'nav.vrit', icon: BedDouble, actions: ['vrit.board'], key: 'v' },
      { id: 'vritHandover', to: '/vrit/handover', label: 'nav.vritHandover', icon: ArrowLeftRight, actions: ['vrit.morning_handover'] },
      { id: 'incidents', to: '/incidents', label: 'nav.incidents', icon: Siren, actions: ['incidents.list'], key: 'x' },
      { id: 'arrivals', to: '/arrivals', label: 'nav.arrivals', icon: DoorOpen, actions: ['arrivals.list'] },
      { id: 'bloodBank', to: '/blood-bank', label: 'nav.bloodBank', icon: Droplets, actions: ['bloodbank.overview'] },
      { id: 'supply', to: '/supply', label: 'nav.supply', icon: Receipt, actions: ['supply.invoices_list'] },
      { id: 'kb', to: '/kb', label: 'nav.kb', icon: BookOpen, actions: ['kb.list'], key: 'b' },
      { id: 'registers', to: '/registers', label: 'nav.registers', icon: Archive, actions: ['registers.list'], key: 'r' },
      { id: 'staff', to: '/staff', label: 'nav.staff', icon: Contact, actions: ['hr.employees_list'] },
      { id: 'staffBriefings', to: '/staff/briefings', label: 'nav.staffBriefings', icon: ShieldCheck, actions: ['hr.employees_list'] },
      { id: 'staffMe', to: '/staff/me', label: 'nav.staffMe', icon: GraduationCap, actions: ['hr.me'], roles: ['admin', 'doctor', 'assistant', 'call_center', 'founder', 'quality'] },
      { id: 'personnel', to: '/personnel', label: 'nav.personnel', icon: IdCard, actions: ['personnel.orders'] },
      { id: 'cash', to: '/cash', label: 'nav.cash', icon: Banknote, actions: ['cashops.today', 'cashops.night_list', 'cashops.discounts', 'cashops.fop_limits', 'cashops.loans'] },
      { id: 'mgmt', to: '/mgmt', label: 'nav.mgmt', icon: Gavel, actions: ['mgmt.meetings_list', 'mgmt.doctor_stats'] },
      { id: 'content', to: '/marketing/content', label: 'nav.content', icon: Newspaper, actions: ['content.articles_list'] },
      { id: 'consents', to: '/consents', label: 'nav.consents', icon: FileSignature, actions: ['consents.list'] },
      { id: 'clinq', to: '/quality/cards', label: 'nav.clinq', icon: ShieldCheck, actions: ['clinq.findings', 'clinq.calls', 'clinq.bodies'] },
    ],
  },
  {
    id: 'admin',
    label: 'nav.admin',
    items: [
      { id: 'implementation', to: '/implementation', label: 'nav.implementation', icon: Rocket, actions: ['rollout.list'] },
      { id: 'users', to: '/admin/users', label: 'nav.users', icon: Users, actions: ['users.list'], key: 'u' },
      { id: 'fops', to: '/finance/fops', label: 'nav.fops', icon: Landmark, actions: ['finance.fops'] },
      { id: 'registry', to: '/registry', label: 'nav.registry', icon: Combine, actions: ['mdm.search'] },
      { id: 'connectors', to: '/admin/connectors', label: 'nav.connectors', icon: Cable, actions: ['connectors.save', 'connectors.test'] },
      { id: 'sync', to: '/admin/sync', label: 'nav.sync', icon: RefreshCw, actions: ['sync.run'] },
      { id: 'jobs', to: '/admin/jobs', label: 'nav.jobs', icon: FileClock, actions: ['sync.run'] },
      { id: 'rooms', to: '/admin/rooms', label: 'nav.rooms', icon: Video, actions: ['rooms.camera_map_set', 'room_sessions.list'] },
      { id: 'operators', to: '/admin/operators', label: 'nav.operators', icon: Bot, actions: ['operators.map'] },
      { id: 'clientapp', to: '/admin/client-app', label: 'nav.clientapp', icon: Smartphone, actions: ['clientapp.booking_requests', 'kiosk.settings_get'] },
      { id: 'settings', to: '/admin/settings', label: 'nav.settings', icon: Cog, actions: ['settings.list'] },
      { id: 'audit', to: '/admin/audit', label: 'nav.audit', icon: ScrollText, actions: ['audit.query'] },
      { id: 'system', to: '/admin/system', label: 'nav.system', icon: ServerCog, actions: ['system.status'] },
    ],
  },
]

export const EXTRA_ROUTES: NavItem[] = [
  { id: 'review', to: '/journeys/review', label: 'nav.linkReview', icon: ClipboardCheck, actions: ['journeys.link_confirm'] },
  { id: 'missed', to: '/calls/missed', label: 'nav.missed', icon: Activity, actions: ['journeys.missed_opportunities'] },
  { id: 'analytics', to: '/finance', label: 'nav.finance', icon: BarChart3, actions: ['finance.revenue'] },
  { id: 'profile', to: '/profile', label: 'nav.profile', icon: KeyRound, key: 'p' },
]

/** May the user call this action (per the manifest permission)? */
export function mayCall(me: Me | undefined, action: ActionId): boolean {
  if (!me) return false
  const perm = ACTION_PERMISSION[action]
  if (!perm) return false
  return perm === 'authenticated' || me.permissions.includes(perm)
}

export function canSee(item: NavItem, me: Me | undefined): boolean {
  if (!me) return false
  const sa = me.roles.includes('super_admin')
  if (sa) return true
  if (item.roles && !item.roles.some((r) => me.roles.includes(r))) return false
  if (item.actions && !item.actions.some((a) => mayCall(me, a))) return false
  return true
}

export function visibleNav(me: Me | undefined): NavSection[] {
  return NAV.map((s) => ({ ...s, items: s.items.filter((i) => canSee(i, me)) })).filter((s) => s.items.length > 0)
}

/** First screen for a user (Today for admin/doctor/founder, otherwise their first section). */
export function homePath(me: Me | undefined): string {
  const first = visibleNav(me)[0]?.items[0]
  return first?.to ?? '/profile'
}
